import { useState, useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import {
  Truck, Plus, Search, Wrench, Box, Loader2, Briefcase, X, Sparkles,
  Wifi, Zap, FileText, MonitorSmartphone, Hammer, Phone, Package
} from "lucide-react";
import axios from "axios";
import { API, useAuth } from "../contexts/AuthContext";
import { formatPYGCompact, formatDate } from "../utils/format";
import { Skeleton } from "../components/ui/skeleton";
import { toast } from "sonner";
import { roleAllowed } from "../utils/roles";

// V4.1 — Operaciones = gastos operativos GENERALES de la empresa (sin obra).
// La IA clasifica la categoría desde la descripción / factura.
const OPE_CATEGORIES = [
  "Servicios Públicos", "Transporte", "Insumos de Oficina", "Equipos",
  "Mantenimiento", "Comunicaciones", "Software/Licencias", "Otros",
];

const CAT_ICONS = {
  "Servicios Públicos": Zap,
  "Transporte": Truck,
  "Insumos de Oficina": Package,
  "Equipos": Box,
  "Mantenimiento": Hammer,
  "Comunicaciones": Wifi,
  "Software/Licencias": MonitorSmartphone,
  "Otros": Briefcase,
};

const ALLOWED_ROLES = ["Superadministrador", "Administrador", "Contador", "Gerencia Financiera", "Gerente Tecnico", "Jefe de Compras"];

function StatCard({ title, value, sub, color = "gray", icon: Icon }) {
  const colors = {
    blue: "bg-blue-50 text-blue-600 border-blue-100",
    emerald: "bg-emerald-50 text-emerald-600 border-emerald-100",
    amber: "bg-amber-50 text-amber-600 border-amber-100",
    orange: "bg-orange-50 text-orange-600 border-orange-100",
  };
  return (
    <div className="bg-white rounded-xl border border-gray-200/60 p-5 shadow-sm hover:shadow-md transition-all">
      <div className="flex items-center justify-between mb-2">
        <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide">{title}</p>
        <div className={`p-2 rounded-lg border ${colors[color] || "bg-gray-50 text-gray-600 border-gray-100"}`}><Icon size={15} /></div>
      </div>
      <p className="text-xl font-bold text-gray-900">{value}</p>
      {sub && <p className="text-xs text-gray-400 mt-0.5">{sub}</p>}
    </div>
  );
}

function NewOpeModal({ open, onClose, onSaved }) {
  const [form, setForm] = useState({
    description: "", amount_pyg: "", provider: "",
    date: new Date().toISOString().split("T")[0], has_iva: true,
  });
  const [invoiceFile, setInvoiceFile] = useState(null);
  const [invoiceB64, setInvoiceB64] = useState(null);
  const [invoiceType, setInvoiceType] = useState(null);
  const [aiResult, setAiResult] = useState(null);  // { category, confidence, reasoning }
  const [categoryOverride, setCategoryOverride] = useState("");
  const [saving, setSaving] = useState(false);
  const fileRef = useRef(null);

  if (!open) return null;

  const handleFile = async (e) => {
    const f = e.target.files?.[0];
    e.target.value = "";
    if (!f) return;
    if (f.size > 5 * 1024 * 1024) { toast.error("Archivo > 5MB"); return; }
    const reader = new FileReader();
    reader.onload = () => {
      setInvoiceB64(reader.result);
      setInvoiceType(f.type);
      setInvoiceFile(f);
    };
    reader.readAsDataURL(f);
  };

  const reset = () => {
    setForm({ description: "", amount_pyg: "", provider: "", date: new Date().toISOString().split("T")[0], has_iva: true });
    setInvoiceFile(null); setInvoiceB64(null); setInvoiceType(null);
    setAiResult(null); setCategoryOverride("");
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.description || !form.amount_pyg) {
      toast.error("Descripción y monto son obligatorios");
      return;
    }
    setSaving(true);
    try {
      const payload = {
        description: form.description,
        amount_pyg: Number(form.amount_pyg),
        date: form.date,
        provider: form.provider,
        has_iva: form.has_iva,
      };
      if (invoiceB64) {
        payload.invoice_file = invoiceB64;
        payload.invoice_file_type = invoiceType;
      }
      if (categoryOverride) {
        payload.category_override = categoryOverride;
      }
      const res = await axios.post(`${API}/purchases/operations`, payload, { withCredentials: true });
      const d = res.data;
      // Mostrar la categoría asignada antes de cerrar
      if (d.ai_confidence < 80 && !categoryOverride) {
        // Confianza baja → invitar a corregir
        setAiResult({ category: d.category, confidence: d.ai_confidence, reasoning: d.ai_reasoning, doc_id: d._id });
        toast.warning(`IA asignó "${d.category}" con confianza ${d.ai_confidence}%. Si querés, ajustá la categoría.`);
        setSaving(false);
        return;
      }
      toast.success(`Gasto operativo registrado — Categoría: ${d.category}`);
      onSaved();
      onClose();
      reset();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error al registrar");
    } finally {
      setSaving(false);
    }
  };

  return createPortal(
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={() => { onClose(); reset(); }} />
      <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-md max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100 sticky top-0 bg-white z-10">
          <div>
            <h2 className="text-lg font-bold text-gray-900">Nuevo Gasto Operativo</h2>
            <p className="text-[11px] text-gray-500">Gastos generales de la oficina · IA clasifica la categoría</p>
          </div>
          <button onClick={() => { onClose(); reset(); }} className="p-2 rounded-lg hover:bg-gray-100"><X size={18} className="text-gray-500" /></button>
        </div>
        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          <div>
            <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1.5">Descripción *</label>
            <textarea
              className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm bg-white focus:ring-2 focus:ring-orange-200 transition-all"
              rows={2}
              value={form.description}
              onChange={e => setForm(f => ({ ...f, description: e.target.value }))}
              placeholder="Ej: Pago factura ANDE marzo / Compra de papelería..."
              required
              data-testid="ope-description-input"
            />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1.5">Monto (PYG) *</label>
              <input type="number" className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm bg-white" value={form.amount_pyg} onChange={e => setForm(f => ({ ...f, amount_pyg: e.target.value }))} required data-testid="ope-amount-input" />
            </div>
            <div>
              <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1.5">Fecha</label>
              <input type="date" className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm bg-white" value={form.date} onChange={e => setForm(f => ({ ...f, date: e.target.value }))} />
            </div>
          </div>
          <div>
            <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1.5">Proveedor</label>
            <input className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm bg-white" value={form.provider} onChange={e => setForm(f => ({ ...f, provider: e.target.value }))} placeholder="Opcional..." />
          </div>

          {/* Factura adjunta */}
          <div className="rounded-xl border border-dashed border-purple-200 bg-purple-50/50 p-3">
            <input ref={fileRef} type="file" accept="image/*,application/pdf" className="hidden" onChange={handleFile} data-testid="ope-invoice-input" />
            <button type="button" onClick={() => fileRef.current?.click()} className="w-full flex items-center justify-center gap-2 bg-white border border-purple-300 hover:bg-purple-100 text-purple-700 rounded-lg py-2 text-sm font-semibold transition-all">
              <FileText size={14} /> {invoiceFile ? `Factura: ${invoiceFile.name}` : "Adjuntar factura (opcional, mejora la IA)"}
            </button>
            <p className="text-[11px] text-purple-600/80 mt-1.5 text-center">PDF o imagen. La IA leerá descripción + factura para clasificar.</p>
          </div>

          {aiResult && (
            <div className="rounded-xl border border-amber-200 bg-amber-50 p-3 text-xs text-amber-800 space-y-2" data-testid="ai-confidence-banner">
              <p className="font-semibold flex items-center gap-1"><Sparkles size={12} /> IA sugiere: {aiResult.category} (confianza {aiResult.confidence}%)</p>
              <p className="italic text-amber-700">{aiResult.reasoning}</p>
              <div>
                <label className="block text-[11px] font-semibold mb-1">Corregir categoría:</label>
                <select className="w-full border border-amber-300 rounded-lg px-2 py-1.5 text-xs bg-white" value={categoryOverride} onChange={e => setCategoryOverride(e.target.value)} data-testid="ope-category-override">
                  <option value="">(Mantener sugerencia de IA)</option>
                  {OPE_CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
                </select>
              </div>
            </div>
          )}

          <button type="submit" disabled={saving} className="w-full bg-[#2E6DA4] hover:bg-[#245A8A] text-white py-2.5 rounded-xl text-sm font-semibold transition-all disabled:opacity-60 flex items-center justify-center gap-2" data-testid="ope-submit-btn">
            {saving ? <Loader2 size={16} className="animate-spin" /> : <Sparkles size={14} />}
            {saving ? "Analizando con IA..." : aiResult ? "Confirmar y Guardar" : "Registrar (IA clasifica)"}
          </button>
        </form>
      </div>
    </div>
  , document.body);
}

export default function ComprasOperaciones() {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [filterCat, setFilterCat] = useState("todas");
  const [showModal, setShowModal] = useState(false);

  const canCreate = roleAllowed(user?.role, ALLOWED_ROLES);

  const fetchData = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/purchases/operations`, { withCredentials: true });
      setItems(res.data || []);
    } catch {
      toast.error("Error al cargar operaciones");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchData(); }, []);

  const filtered = items.filter(t => {
    const matchSearch = !search || t.description?.toLowerCase().includes(search.toLowerCase()) || t.supplier?.toLowerCase().includes(search.toLowerCase());
    const matchCat = filterCat === "todas" || t.category === filterCat;
    return matchSearch && matchCat;
  });

  const totalMes = items.filter(t => {
    const d = new Date(t.date); const now = new Date();
    return d.getMonth() === now.getMonth() && d.getFullYear() === now.getFullYear();
  }).reduce((a, t) => a + (t.amount_pyg || 0), 0);

  const byCategory = OPE_CATEGORIES.map(cat => ({
    cat,
    icon: CAT_ICONS[cat] || Briefcase,
    total: items.filter(t => t.category === cat).reduce((a, t) => a + (t.amount_pyg || 0), 0),
    count: items.filter(t => t.category === cat).length,
  }));

  return (
    <div className="p-4 sm:p-6 lg:p-8 space-y-6">
      <div>
        <div className="flex items-center gap-2 mb-1">
          <span className="text-xs font-bold text-orange-600 bg-orange-50 px-2 py-0.5 rounded-full uppercase tracking-wide">Compras</span>
        </div>
        <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 tracking-tight">Costos indirectos (OPE)</h1>
        <p className="text-sm text-gray-500 mt-0.5">Gastos operativos generales de la empresa · IA clasifica la categoría · cada gasto se paga desde Tesorería → Pagos Serv. Tercerizados · los gastos indirectos de Caja Chica ya salieron de la caja</p>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard title="Total Registros" value={items.length} color="orange" icon={Briefcase} />
        <StatCard title="Este Mes" value={formatPYGCompact(totalMes)} color="blue" icon={Truck} />
        {byCategory.slice(0, 2).map(({ cat, count, total, icon }) => (
          <StatCard key={cat} title={cat} value={formatPYGCompact(total)} sub={`${count} registros`} color="amber" icon={icon} />
        ))}
      </div>

      {/* Category breakdown */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {byCategory.map(({ cat, total, count, icon: CatIcon }) => (
          <div key={cat} className={`bg-white rounded-xl border p-4 cursor-pointer transition-all ${filterCat === cat ? "border-orange-400 shadow-md" : "border-gray-200/60 hover:border-gray-300"}`} onClick={() => setFilterCat(filterCat === cat ? "todas" : cat)} data-testid={`ope-cat-${cat.toLowerCase().replace(/[\s/]+/g, "-")}`}>
            <div className="flex items-center justify-between mb-1">
              <p className="text-xs font-bold text-gray-500 uppercase tracking-wide truncate">{cat}</p>
              <CatIcon size={14} className="text-orange-500 shrink-0" />
            </div>
            <p className="text-lg font-bold text-gray-900 mt-1">{formatPYGCompact(total)}</p>
            <p className="text-xs text-gray-400">{count} registros</p>
          </div>
        ))}
      </div>

      <div className="flex flex-col sm:flex-row gap-3">
        <div className="relative flex-1 max-w-sm">
          <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
          <input className="w-full pl-9 pr-3 py-2 text-sm border border-gray-200 rounded-xl bg-white focus:ring-2 focus:ring-orange-200 transition-all" placeholder="Buscar operación..." value={search} onChange={e => setSearch(e.target.value)} data-testid="operaciones-search" />
        </div>
        {canCreate && (
          <button onClick={() => setShowModal(true)} className="flex items-center gap-2 bg-[#2E6DA4] hover:bg-[#245A8A] text-white px-4 py-2.5 rounded-xl text-sm font-semibold transition-all shadow-sm" data-testid="new-ope-btn">
            <Plus size={15} /> Nuevo Gasto
          </button>
        )}
      </div>

      <div className="bg-white rounded-2xl border border-gray-200/60 shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-gray-100 bg-gray-50/50">
                <th className="text-left px-5 py-3 text-xs font-bold text-gray-500 uppercase tracking-wide">Fecha</th>
                <th className="text-left px-5 py-3 text-xs font-bold text-gray-500 uppercase tracking-wide">Proveedor / Descripción</th>
                <th className="text-left px-5 py-3 text-xs font-bold text-gray-500 uppercase tracking-wide">Categoría</th>
                <th className="text-right px-5 py-3 text-xs font-bold text-gray-500 uppercase tracking-wide">Monto</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                Array.from({ length: 4 }).map((_, i) => (
                  <tr key={i} className="border-b border-gray-50">
                    {Array.from({ length: 4 }).map((__, j) => <td key={j} className="px-5 py-4"><Skeleton className="h-4 w-full" /></td>)}
                  </tr>
                ))
              ) : filtered.length === 0 ? (
                <tr><td colSpan={4} className="text-center py-12 text-gray-400 text-sm"><Truck size={32} className="mx-auto mb-2 opacity-30" />No hay gastos operativos registrados</td></tr>
              ) : (
                filtered.map((t, i) => (
                  <tr key={t._id || i} className="border-b border-gray-50 hover:bg-gray-50/50 transition-colors" data-testid="ope-row">
                    <td className="px-5 py-3.5 text-sm text-gray-500 whitespace-nowrap">{formatDate(t.date)}</td>
                    <td className="px-5 py-3.5"><p className="text-sm font-medium text-gray-900">{t.supplier || "—"}</p><p className="text-xs text-gray-400 truncate max-w-[280px]">{t.description}</p></td>
                    <td className="px-5 py-3.5">
                      <span className="text-xs font-semibold px-2 py-0.5 rounded-full bg-orange-100 text-orange-700">{t.category}</span>
                      {/* Fix FZ (2026-10-14): el gasto indirecto cargado en Caja Chica, ya pagado desde la caja. */}
                      {t.fuente === "caja_chica" && (
                        <span className="ml-1 text-xs font-semibold px-2 py-0.5 rounded-full bg-violet-100 text-violet-700" data-testid="ope-fuente-caja-chica"
                          title="Pagado desde Caja Chica: no pasa por Tesorería">
                          Caja Chica{t.petty_cash_numero ? ` · ${t.petty_cash_numero}` : ""}
                        </span>
                      )}
                      {typeof t.ai_confidence === "number" && t.ai_confidence < 80 && (
                        <span className="ml-1 text-[10px] text-amber-600" title={t.ai_reasoning}>IA {t.ai_confidence}%</span>
                      )}
                    </td>
                    <td className="px-5 py-3.5 text-right text-sm font-bold text-gray-900">{formatPYGCompact(t.amount_pyg)}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
      <NewOpeModal open={showModal} onClose={() => setShowModal(false)} onSaved={fetchData} />
    </div>
  );
}
